import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import apiService from "../../shared/apiService";
import { useTranslation } from "../../contexts/LanguageContext";
import Skeleton from "../ui/Skeleton";

interface DemographicsProps {
  campaignId: string;
}

interface DemoData {
  pivotValue: {
    urn: string;
    value?: string;
  };
  clicks: number;
  impressions: number;
}

export const LinkedInDemographics: React.FC<DemographicsProps> = ({
  campaignId,
}) => {
  const { t } = useTranslation();
  const [dataSize, setDataSize] = useState<any[]>([]);
  const [dataJob, setDataJob] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [resSize, resJob] = await Promise.all([
          apiService.get<any>(
            `/api/ads/campaigns/${campaignId}/linkedin/demographics?pivot=MEMBER_COMPANY_SIZE`,
          ),
          apiService.get<any>(
            `/api/ads/campaigns/${campaignId}/linkedin/demographics?pivot=MEMBER_JOB_FUNCTION`,
          ),
        ]);

        if (resSize && resSize.success && resSize.data) {
          const parsed = resSize.data.map((d: DemoData) => ({
            name: d.pivotValue?.value || d.pivotValue?.urn || "Desconhecido",
            clicks: d.clicks || 0,
            impressions: d.impressions || 0,
          }));
          setDataSize(parsed);
        }

        if (resJob && resJob.success && resJob.data) {
          const parsed = resJob.data.map((d: DemoData) => ({
            name: d.pivotValue?.value || d.pivotValue?.urn || "Desconhecido",
            clicks: d.clicks || 0,
            impressions: d.impressions || 0,
          }));
          setDataJob(parsed);
        }
      } catch (err) {
        console.error("Erro ao carregar dados demográficos do LinkedIn", err);
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) {
      fetchData();
    }
  }, [campaignId]);

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="bg-white p-3 rounded-xl shadow-xs border border-purple-200 flex flex-col"
          >
            <Skeleton
              variant="text"
              width="55%"
              height="0.75rem"
              className="mb-3"
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={224}
              className="rounded-lg"
            />
          </div>
        ))}
      </div>
    );
  }

  if (dataSize.length === 0 && dataJob.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-6 h-[250px] bg-[#0e76a8]/5 border border-dashed border-[#0e76a8]/20 rounded-xl">
        <div className="w-12 h-12 bg-white rounded-xl border border-[#0e76a8]/20 shadow-xs flex items-center justify-center mb-3">
          <svg
            viewBox="0 0 24 24"
            className="h-6 w-6 text-[#0e76a8] opacity-80"
            fill="currentColor"
          >
            <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a1.66 1.66 0 000 1.2V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
          </svg>
        </div>
        <p className="text-sm font-bold text-gray-700 text-center">
          {t("Dados Demográficos Indisponíveis")}
        </p>
        <p className="text-[11px] text-gray-400 text-center mt-1 max-w-[250px] leading-relaxed">
          {t(
            "Esta campanha ainda não gerou impressões ou interações suficientes para exibir informações demográficas de perfil.",
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Gráfico Tamanho da Empresa */}
      <div className="bg-white p-3 rounded-xl shadow-xs border border-purple-200 flex flex-col">
        <h3 className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wider">
          {t("Cliques por Tamanho da Empresa")}
        </h3>
        <div className="h-56 min-h-[224px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={dataSize}
              layout="vertical"
              margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                stroke="#f3f4f6"
              />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={100}
                tick={{ fontSize: 10, fill: "#4b5563", fontWeight: 600 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
                cursor={{ fill: "#f3f4f6" }}
              />
              <Bar
                dataKey="clicks"
                fill="#0e76a8"
                name="Cliques"
                radius={[0, 4, 4, 0]}
                barSize={20}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Gráfico Cargo / Função */}
      <div className="bg-white p-3 rounded-xl shadow-xs border border-purple-200 flex flex-col">
        <h3 className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wider">
          {t("Cliques por Função/Cargo")}
        </h3>
        <div className="h-56 min-h-[224px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={dataJob}
              layout="vertical"
              margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                stroke="#f3f4f6"
              />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={100}
                tick={{ fontSize: 10, fill: "#4b5563", fontWeight: 600 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
                cursor={{ fill: "#f3f4f6" }}
              />
              <Bar
                dataKey="clicks"
                fill="#10b981"
                name="Cliques"
                radius={[0, 4, 4, 0]}
                barSize={20}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
